import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';
import { useAuth } from '../contexts/AppContext';
import { Heart, ListMusic, User, ChevronRight, Plus, LogIn, Shield, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';

const API = process.env.REACT_APP_BACKEND_URL;
const EYE_ICON_URL = "/sidebar-logo.png";

// Admin email whitelist
const ADMIN_EMAILS = ['modtest@aivue.com', 'admin@audivue.com', 'cpowersv@gmail.com'];

export const LibraryPage = () => {
  const { user, token, axiosInstance } = useAuth();
  const navigate = useNavigate();
  const [likes, setLikes] = useState([]);
  const [playlists, setPlaylists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreatePlaylist, setShowCreatePlaylist] = useState(false);
  const [newPlaylistName, setNewPlaylistName] = useState('');
  const [creating, setCreating] = useState(false);
  
  const isAdmin = user && ADMIN_EMAILS.includes(user.email);


  const fetchUserData = useCallback(async () => {
    try {
      // Fetch liked videos
      const likesRes = await axiosInstance.get('/favorites');
      setLikes(likesRes.data || []);
      
      // Fetch playlists
      const playlistsRes = await axiosInstance.get('/playlists');
      setPlaylists(playlistsRes.data || []);
    } catch (error) {
      console.error('Error fetching library data:', error);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    if (token) {
      fetchUserData();
    } else {
      setLoading(false);
    }
  }, [token, fetchUserData]);

  const createPlaylist = async () => {
    if (!newPlaylistName.trim()) {
      toast.error('Please enter a playlist name');
      return;
    }
    setCreating(true);
    try {
      await axiosInstance.post('/playlists', { name: newPlaylistName.trim() });
      toast.success('Playlist created!');
      setNewPlaylistName('');
      setShowCreatePlaylist(false);
      fetchUserData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create playlist');
    } finally {
      setCreating(false);
    }
  };

  const getThumbnail = (video) => {
    // Default fallback → AUDIVUE branded placeholder (set centrally in resolveThumbnailUrl).
    return resolveThumbnailUrl(video);
  };

  // Not logged in state
  if (!token) {
    return (
      <div className="min-h-screen bg-black px-5 pt-6 pb-32">
        <div className="flex items-center gap-3 mb-8">
          <img src={EYE_ICON_URL} alt="AUDIVUE" className="w-10 h-10 rounded-xl shadow-lg" />
          <h1 className="text-2xl font-bold text-white">Your Library</h1>
        </div>
        
        <div className="flex flex-col items-center justify-center py-16 text-center">
          <div className="w-20 h-20 rounded-full bg-zinc-800 flex items-center justify-center mb-4">
            <LogIn className="w-10 h-10 text-zinc-500" />
          </div>
          <h2 className="text-xl font-semibold text-white mb-2">Sign in to see your library</h2>
          <p className="text-zinc-500 mb-6">Save your favorite videos and create playlists</p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate(`/register?next=${encodeURIComponent('/library')}`)}
              className="px-8 py-3 bg-white text-black font-semibold rounded-full hover:bg-zinc-200 transition-colors"
              data-testid="library-signup-btn"
            >
              Sign Up Free
            </button>
            <button
              onClick={() => navigate(`/login?next=${encodeURIComponent('/library')}`)}
              className="px-8 py-3 border border-zinc-700 text-white font-medium rounded-full hover:bg-zinc-800 transition-colors"
              data-testid="library-signin-btn"
            >
              Sign In
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-10 h-10 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black px-5 pt-6 pb-32">
      <div className="flex items-center gap-3 mb-6">
        <img src={EYE_ICON_URL} alt="AUDIVUE" className="w-10 h-10 rounded-xl shadow-lg" />
        <h1 className="text-2xl font-bold text-white">Your Library</h1>
      </div>

      {/* Liked Videos Section */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/favorites')}
          className="w-full flex items-center gap-4 p-4 bg-zinc-900/50 rounded-xl hover:bg-zinc-800/50 transition-colors"
          data-testid="library-likes-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-pink-500 to-red-500 flex items-center justify-center">
            <Heart className="w-7 h-7 text-white fill-white" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-semibold text-white">Liked Videos</p>
            <p className="text-sm text-zinc-500">{likes.length} videos</p>
          </div>
          <ChevronRight className="w-5 h-5 text-zinc-500" />
        </button>

        {/* Preview of liked videos */}
        {likes.length > 0 && (
          <div className="flex gap-2 mt-3 overflow-x-auto" style={{ scrollbarWidth: 'none' }}>
            {likes.slice(0, 4).map((video) => (
              <button
                key={video.id}
                onClick={() => navigate(`/video/${video.id}`)}
                className="flex-shrink-0 w-24 h-24 rounded-lg overflow-hidden bg-zinc-800"
              >
                <img
                  src={getThumbnail(video)}
                  alt={video.title}
                  className="w-full h-full object-cover"
                />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Playlists Section */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold text-white">Playlists</h2>
          <button
            onClick={() => setShowCreatePlaylist(true)}
            className="flex items-center gap-1 text-sm text-orange-400 hover:text-orange-300"
            data-testid="create-playlist-btn"
          >
            <Plus className="w-4 h-4" />
            Create
          </button>
        </div>

        {playlists.length === 0 ? (
          <div className="p-6 bg-zinc-900/50 rounded-xl text-center">
            <ListMusic className="w-10 h-10 text-zinc-600 mx-auto mb-2" />
            <p className="text-zinc-500 text-sm">No playlists yet</p>
            <p className="text-zinc-600 text-xs mt-1">Create your first playlist to organize your videos</p>
          </div>
        ) : (
          <div className="space-y-2">
            {playlists.map((playlist) => (
              <button
                key={playlist.id}
                onClick={() => navigate('/playlists', { state: { playlistId: playlist.id } })}
                className="w-full flex items-center gap-4 p-3 bg-zinc-900/50 rounded-xl hover:bg-zinc-800/50 transition-colors"
              >
                <div className="w-12 h-12 rounded-lg bg-zinc-800 flex items-center justify-center">
                  <ListMusic className="w-6 h-6 text-zinc-500" />
                </div>
                <div className="flex-1 text-left">
                  <p className="font-medium text-white">{playlist.name}</p>
                  <p className="text-xs text-zinc-500">{playlist.video_count || 0} videos</p>
                </div>
                <ChevronRight className="w-5 h-5 text-zinc-500" />
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Admin Dashboard - only for admins */}
      {isAdmin && (
        <button
          onClick={() => navigate('/admin')}
          className="w-full flex items-center gap-4 p-4 mt-4 bg-orange-500/10 border border-orange-500/30 rounded-xl hover:bg-orange-500/20 transition-colors"
          data-testid="library-admin-btn"
        >
          <div className="w-14 h-14 rounded-xl bg-orange-500/20 flex items-center justify-center">
            <Shield className="w-7 h-7 text-orange-400" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-semibold text-orange-400">Admin Dashboard</p>
            <p className="text-sm text-orange-300/60">Manage users, videos & platform</p>
          </div>
          <ChevronRight className="w-5 h-5 text-orange-400" />
        </button>
      )}

      {/* My Profile at bottom */}
      <button
        onClick={() => navigate('/profile')}
        className="w-full flex items-center gap-4 p-4 mt-6 bg-zinc-900/50 rounded-xl hover:bg-zinc-800/50 transition-colors"
        data-testid="library-profile-btn"
      >
        <div className="w-14 h-14 rounded-full bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center overflow-hidden">
          {user?.avatar ? (
            <img src={`${API}/api/files/${user.avatar}`} alt={user.username} className="w-full h-full object-cover" />
          ) : (
            <User className="w-7 h-7 text-white" />
          )}
        </div>
        <div className="flex-1 text-left">
          <p className="font-semibold text-white">{user?.display_name || user?.username || 'My Profile'}</p>
          <p className="text-sm text-zinc-500">View and edit your profile</p>
        </div>
        <ChevronRight className="w-5 h-5 text-zinc-500" />
      </button>

      {/* Create Playlist Modal */}
      {showCreatePlaylist && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-zinc-900 rounded-2xl w-full max-w-sm p-6 border border-zinc-800">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white">Create Playlist</h3>
              <button onClick={() => setShowCreatePlaylist(false)} className="text-zinc-500 hover:text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <Input
              type="text"
              placeholder="Playlist name"
              value={newPlaylistName}
              onChange={(e) => setNewPlaylistName(e.target.value)}
              className="bg-zinc-800 border-zinc-700 text-white mb-4"
              autoFocus
              data-testid="playlist-name-input"
            />
            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setShowCreatePlaylist(false)}
                className="flex-1 bg-transparent border-zinc-700 text-white hover:bg-zinc-800"
              >
                Cancel
              </Button>
              <Button
                onClick={createPlaylist}
                disabled={creating || !newPlaylistName.trim()}
                className="flex-1 bg-white text-black hover:bg-zinc-200"
                data-testid="create-playlist-submit"
              >
                {creating ? 'Creating...' : 'Create'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
